import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Loadable from "react-loadable";
import { ApolloProvider } from "@apollo/client/react";
import { ApolloClient, ApolloLink, CombinedGraphQLErrors, HttpLink, InMemoryCache, makeVar, ServerError } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { SetContextLink } from "@apollo/client/link/context";
import axios from "axios";
import { routes } from "./Route";
import { authRoutes } from "./AuthRoute";
import { withLayout } from "helpers/hoc/withLayout";
import { AppContextProvider } from "helpers/contexts/AppContext";
import { AuthService } from "services";
import Loader from "components/Loader";
import { Provider } from "react-redux";
import store from "store/store";
import { ModeType, PATH_MODE_VARIABLE } from "./helpers";
import NotFound404 from "./pages/error/NotFound404";
import { useIdleTimer } from "react-idle-timer";
import Cookie from "components/cookie/Cookie";
import CheckMaintenance from "components/check-maintenance/CheckMaintenance";
import Maintenance from "components/check-maintenance/Maintenance";
import SignalR from "components/signal-r/SignalR";
import { ConfigProvider } from "antd";
import { StyleProvider } from "@ant-design/cssinjs";
import ReactGA from "react-ga4";
import { ToastContainer } from "react-toastify";
import SwitchAccountDeniedModal from "components/modal/SwitchAccountDeniedModal";
import { isSwitchAccountDeniedResponse } from "helpers/utils/switchAccountDenied";
import { triggerDeniedModal, useSwitchDeniedModal } from "helpers/hooks/useSwitchDeniedModal";

const loading = () => <Loader />;

declare global {
  interface Window {
    REACT_APP_GRAPHQL_URL?: null;
    REACT_APP_IDENTITY_URL?: null;
    REACT_APP_CMS_URL?: null;
    REACT_APP_API_GATEWAY_URL?: null;
  }
}

axios.interceptors.request.use(async (config) => {
  const token = await AuthService.getAccessToken();
  config.headers.authorization = token ? `Bearer ${token}` : "";
  return config;
});

const graphQLLink = new HttpLink({
  uri: window.REACT_APP_GRAPHQL_URL + "/graphql",
});

const authLink = new SetContextLink(async (prevContext, operation) => {
  const token = await AuthService.getAccessToken();
  return {
    headers: {
      ...prevContext.headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});


type GraphQlGatewayResponse = {
  ResponseCode?: number;
  ErrorMessage?: string;
};

const errorLink = new ErrorLink(({ error, operation }) => {
  if (ServerError.is(error)) {
    try {
      const body = JSON.parse(error.bodyText) as GraphQlGatewayResponse;
      if (body?.ResponseCode === 401) {
        if (operation.operationName === "getMaintenanceStatus") {
          localStorage.clear();
          window.location.href = window.REACT_APP_IDENTITY_URL;
        } else if (isSwitchAccountDeniedResponse(error.bodyText)) {
            globalErrorVar({isSwitchAccountDeniedResponse: true})
        } else {
          AuthService.signOut();
        }
      }
    } catch {
      // Ignore JSON parse error
    }
  }

  if (CombinedGraphQLErrors.is(error)) {
    for (let err of error.errors) {
      if (
        err.extensions.code === "AUTH_NOT_AUTHORIZED" &&
        err.path.length > 0 &&
        err.path[0] !== "getTokenForNotificationHub" &&
        localStorage.getItem("isLogout") === "true"
      ) {
        window.location.reload();
      }
    }
  }
});

export interface IGraphGlobalVar {
 isSwitchAccountDeniedResponse: boolean;
}

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(graphQLLink)]),
  cache: new InMemoryCache(),
});
export const globalErrorVar = makeVar<IGraphGlobalVar>(null);

const AuthLayout = Loadable({
  loader: () => import("components/layout/AuthLayout"),
  render(loaded, props) {
    let Component = loaded.default;
    return <Component {...props} />;
  },
  loading,
});

const NonAuthLayout = Loadable({
  loader: () => import("components/layout/NonAuthLayout"),
  render(loaded, props) {
    let Component = loaded.default;
    return <Component {...props} />;
  },
  loading,
});

const App = () => {
  const [maintenance, setMaintenance] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);

  const handleOnIdle = () => {
    localStorage.clear();
    window.location.reload();
  };

  useIdleTimer({
    timeout: 1000 * 60 * 30,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  useEffect(() => {
    ReactGA.initialize("G-TTFL80ESK7");
    if (["/auth/autologin", "/switch-account"].includes(window.location.pathname)) {
      localStorage.clear();
    }
  }, []);

  const wrapLayout = (route: any) => {
    const Wrapped = withLayout(
      route.useAuthLayout ? AuthLayout : NonAuthLayout,
      route,
      loading
    )(route.component);

    return {
      ...route,
      element: <Wrapped />
    };
  }

  const preparedAuthRoutes = authRoutes.map(r => wrapLayout(r));
  const preparedRoutes = routes
    .map(route => ({...route, modeTypes: route.modeTypes ?? [ModeType.Cattle, ModeType.Sheep]}))
    .map((r) => wrapLayout(r));

  const DashboardRedirectRoute = withLayout(
      NonAuthLayout,
      {title: ""},
      loading,
      '/c/dashboard',
      true
    )();
  const NotFoundRedirectRoute = withLayout(
    NonAuthLayout,
    {title: "notfound404"},
    loading,
  )(NotFound404);
  return (
    <>
      <BrowserRouter>
        <Provider store={store}>
          <AppContextProvider>
            <ApolloProvider client={client}>
              <ConfigProvider
                theme={{
                  token: {
                    fontFamily: "Ubuntu",
                    colorPrimary: "#0a12bf",
                    fontSize: 16,
                  },
                  components: {
                    Table: {
                      bodySortBg: "none",
                    },
                  },
                }}>
                <StyleProvider hashPriority="high">
                  <SignalR />
                  <CheckMaintenance setMaintenance={setMaintenance} setLoadingPage={setLoadingPage} />
                  {loadingPage && <></>}
                  {!loadingPage && (
                    <>
                      {maintenance ? (
                        <Maintenance />
                      ) : (
                        <>
                          <Cookie />
                           <ToastContainer
                              position="bottom-right"
                              autoClose={5000}
                              hideProgressBar={false}
                              newestOnTop={false}
                              closeOnClick
                              rtl={false}
                              pauseOnFocusLoss={false}
                              draggable
                              pauseOnHover
                            />
                          <SwitchAccountDeniedModal />
                          <Routes>
                            {preparedAuthRoutes.map((route, index) => {
                              return (
                                <Route
                                  key={index}
                                  path={route.exact ? route.path : `${route.path}/*`}
                                  element={route.element}
                                />
                              );
                            })}
                            {preparedRoutes.map((route, index) => {
                              return (
                                <Route
                                  key={index}
                                  path={`/:${PATH_MODE_VARIABLE}${route.exact ? route.path : `${route.path}/*`}`}
                                  element={route.element}
                                />
                              );
                            })}
                            <Route
                              path={`/`}
                              element={<DashboardRedirectRoute />}
                            />
                            <Route
                              path={`*`}
                              element={<NotFoundRedirectRoute />}
                            />
                          </Routes>
                        </>
                      )}
                    </>
                  )}
                </StyleProvider>
              </ConfigProvider>
            </ApolloProvider>
          </AppContextProvider>
        </Provider>
      </BrowserRouter>
    </>
  );
};

export default App;
